import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  arePropertyRatesLoading,
  getCheckoutDate,
  getPropertyMappedBedbankOffer,
  getPropertyPackageOptionsByRoom,
} from 'selectors/offerPage/offerPageSelectors'
import type { RateView, RoomPackageOptions } from 'components/OfferPage/Property/singlePropertyUtils'
import { getCombinedRates } from 'components/OfferPage/Property/singlePropertyUtils'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import { initialRooms } from 'lib/offer/occupancyUtils'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import _BaseHotelFlightsToggleButton from './_BaseHotelFlightsToggleButton'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

interface Props {
  offer: App.HotelOffer;
  bedbankOffer?: App.BedbankOffer;
  packageOptionsByRoom: Array<RoomPackageOptions>;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  checkOut?: string;
  duration?: number;
  previewMode: boolean;
  flightOrigin?: App.Airport;
  isLoading: boolean;
  utmContent?: string;
}

function SinglePropertyHotelFlightsToggleButton(props: Props) {
  const {
    offer,
    bedbankOffer,
    packageOptionsByRoom,
    rooms,
    checkIn,
    checkOut,
    duration,
    previewMode,
    flightOrigin,
    isLoading,
    utmContent,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const bedbankFlightPrice = useBedbankFlightPrice(bedbankOffer, {
    rooms,
    startDate: checkIn,
    endDate: checkOut,
    originAirportCode: flightOrigin?.code,
  })

  const combinedOptions = useMemo(() => packageOptionsByRoom.flatMap(room => room.lpc), [packageOptionsByRoom])

  const options = useMemo(() => {
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !isLoading
    return combinedOptions.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, combinedOptions, isLoading])

  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
    },
  )

  const cheapestRoomRates = useMemo(() => packageOptionsByRoom.map(room => {
    const combinedRates = getCombinedRates(
      room.lpc,
      room.bedbank,
      availableOptions,
      {
        previewMode,
        utmContent,
      },
    )

    // Find cheapest rate for this room
    return combinedRates.filter(rate => rate.price > 0).reduce((cheapest: RateView, rate: RateView) => {
      if (!cheapest) return rate
      if (rate.type === 'LPC') {
        if ((rate.lpcRate?.hotelPrice ?? rate.price) < cheapest.price) return rate
      } else {
        if ((rate.bedbankRate?.rate.totals.total ?? rate.price) < cheapest.price) return rate
      }
      return cheapest
    }, null)
  }), [packageOptionsByRoom, previewMode, utmContent, availableOptions])

  const { hotelPrice, hotelMemberPrice, hotelFlightPrice } = useMemo(() => {
    const cheapestRate = cheapestRoomRates.filter(rate => !!rate).sort((a, b) => a.price - b.price)[0]

    if (!cheapestRate) return { hotelPrice: 0, hotelMemberPrice: 0, hotelFlightPrice: 0 }

    if (cheapestRate.type === 'bedbank') {
      const hotelPrice = cheapestRate.bedbankRate?.rate.totals.total ?? cheapestRate.price
      const hotelMemberPrice = cheapestRate.bedbankRate?.rate.totals.memberTotal ?? 0

      return {
        hotelPrice,
        hotelMemberPrice,
        hotelFlightPrice: hotelMemberPrice > 0 ? hotelMemberPrice + (bedbankFlightPrice ?? 0) : hotelPrice + (bedbankFlightPrice ?? 0),
      }
    }

    const hotelPrice = cheapestRate.lpcRate?.hotelPrice ?? cheapestRate.price
    const hotelMemberPrice = cheapestRate.lpcRate?.hotelMemberPrice ?? 0
    const flightPrice = cheapestRate.lpcRate?.flightPrice ?? 0

    return {
      hotelPrice,
      hotelMemberPrice,
      hotelFlightPrice: hotelMemberPrice > 0 ? hotelMemberPrice + flightPrice : hotelPrice + flightPrice,
    }
  }, [cheapestRoomRates, bedbankFlightPrice])

  const showMemberPrice = hotelMemberPrice > 0
  const hotelTotalPrice = useMemo(() => showMemberPrice ? hotelMemberPrice : hotelPrice, [showMemberPrice, hotelMemberPrice, hotelPrice])

  const flightsAvailable = offer.offerFlightsEnabled && !offer.flightCacheDisabled

  return <_BaseHotelFlightsToggleButton
    flightsAvailable={flightsAvailable}
    isMemberPrice={showMemberPrice}
    hotelTotalPrice={hotelTotalPrice}
    hotelFlightTotalPrice={hotelTotalPrice !== hotelFlightPrice ? hotelFlightPrice : undefined}
    pricesLoading={isLoading}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer as App.HotelOffer,
  bedbankOffer: getPropertyMappedBedbankOffer(state, appState),
  packageOptionsByRoom: getPropertyPackageOptionsByRoom(state, appState),
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  duration: state.duration,
  previewMode: appState.system.previewMode,
  flightOrigin: state.airport,
  isLoading: arePropertyRatesLoading(state, appState),
  utmContent: appState.router.location.query.utm_content,
  open: !!state.activeFilter,
}), SinglePropertyHotelFlightsToggleButton, preventRenderWithOpenFilter)
